// Loads and indexes the recorded fixtures bundle (public/demo-fixtures.json).
//
// The bundle is a flat map of "METHOD /path?query" (see signature.ts) -> the
// captured JSON response. We build two lookups: an exact-signature map and a
// path-only fallback map so a page still renders something when the visitor
// triggers a query combination that was never recorded.

import { FIXTURES_URL } from './config';
import { pathSignature } from './signature';

export type Fixture = {
  status: number;
  body: unknown;
};

type RawFixture = { status?: number; body?: unknown } | unknown;

let exact: Map<string, Fixture> = new Map();
let byPath: Map<string, Fixture> = new Map();
let loaded = false;

function coerce(raw: RawFixture): Fixture {
  // Accept both the rich `{ status, body }` shape and a bare body (older/hand
  // written fixtures), so the file is forgiving to edit by hand.
  if (raw && typeof raw === 'object' && 'body' in (raw as Record<string, unknown>)) {
    const r = raw as { status?: number; body?: unknown };
    return { status: typeof r.status === 'number' ? r.status : 200, body: r.body };
  }
  return { status: 200, body: raw };
}

export async function loadFixtures(): Promise<void> {
  if (loaded) return;
  loaded = true;
  try {
    const res = await fetch(FIXTURES_URL, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as Record<string, RawFixture>;
    for (const [key, raw] of Object.entries(data)) {
      const fixture = coerce(raw);
      exact.set(key, fixture);
      // First write wins for the path fallback (keeps the most "default" record).
      const [method, sigRest] = key.split(' ', 2);
      const path = (sigRest ?? '').split('?', 1)[0];
      const pathKey = `${method} ${path}`;
      if (!byPath.has(pathKey)) byPath.set(pathKey, fixture);
    }
    console.info(`[demo] loaded ${exact.size} fixtures`);
  } catch (err) {
    console.warn(
      `[demo] could not load ${FIXTURES_URL} — pages will fall back to empty data. ` +
        `Did you run the recorder and place demo-fixtures.json in public/?`,
      err
    );
  }
}

export function lookupFixture(exactKey: string, pathKey: string): Fixture | null {
  return exact.get(exactKey) ?? byPath.get(pathKey) ?? null;
}

// Exposed for the translation lookup, which scans recorded payloads for
// { term, translation } pairs.
export function allFixtures(): Fixture[] {
  return [...exact.values()];
}

export { pathSignature };
